import { doc, getDoc } from 'firebase/firestore';

import { getFirestore } from 'services/Firebase';

import { type UserSchemaPublic } from './User';

type GetUserOptions = {
    public?: boolean;
    refetch?: boolean;
};

class CachedUser {
    data: UserSchemaPublic;

    constructor(data: UserSchemaPublic) {
        this.data = data;
    }
}

class UsersCache {
    usersMap: Map<string, CachedUser>;

    constructor() {
        this.usersMap = new Map();
    }

    fetchById = async (id: string, options: GetUserOptions) => {
        const docRef = options?.public
            ? doc(getFirestore(), 'users', id, 'public', 'info')
            : doc(getFirestore(), 'users', id);

        const data = (await getDoc(docRef)).data();
        if (!data) return null;

        const serializedRecord: UserSchemaPublic = {
            department: data.department,
            displayName: data.displayName,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            cachedPhotoURL: data.cachedPhotoURL,
            photoURL: data.photoURL,
            uid: id,
        };

        const cachedUser = new CachedUser(serializedRecord);
        this.usersMap.set(id, cachedUser);
        return cachedUser;
    };

    getById = async (
        id: string,
        options: GetUserOptions = { public: true, refetch: false }
    ): Promise<CachedUser | null> => {
        if (options?.refetch) {
            return await this.fetchById(id, options);
        }

        const cachedUser = this.usersMap.get(id);
        if (cachedUser) {
            return cachedUser;
        }

        const fetchedUser = await this.fetchById(id, options);
        if (!fetchedUser) {
            throw new Error(`User with ID ${id} not found`);
        }
        return fetchedUser;
    };
}

export default UsersCache;
